import type { BotLevel, Opponent } from '@sea-wars/protocol';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useChallenges } from '../api/challenges.js';
import { api } from '../api/client.js';
import { useSession } from '../api/session.js';
import { PlayersCard } from '../components/PlayersCard.js';

const BOT_LEVEL_LABEL: Record<BotLevel, string> = {
  easy: 'Łatwy',
  medium: 'Średni',
  hard: 'Trudny',
};

export function LobbyPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [startingBot, setStartingBot] = useState(false);
  const botDialogRef = useRef<HTMLDialogElement>(null);
  const [opponents, setOpponents] = useState<Opponent[] | null>(null);
  const [busyOpponentId, setBusyOpponentId] = useState<string | null>(null);
  // Obie karty (Przeciwnicy i Gracze) pokazują gwiazdkę ulubionych — przełączenie w jednej
  // musi być widoczne w drugiej.
  const [playersRefreshKey, setPlayersRefreshKey] = useState(0);
  // Wyzwania (docs/08-decyzje.md #21) żyją w `ChallengesProvider` na poziomie całej appki —
  // przychodzące pokazuje globalny `NotificationPopup`, tu potrzebne są tylko wysłane.
  const { outgoing, challenge: sendChallenge, cancel: cancelChallenge } = useChallenges();

  useEffect(() => {
    api
      .opponents()
      .then((response) => setOpponents(response.items))
      .catch(() => setOpponents([]));
  }, []);

  async function startBotGame(level: BotLevel) {
    setStartingBot(true);
    try {
      const room = await api.createBotRoom(level);
      botDialogRef.current?.close();
      navigate(`/game/${room.gameId}`);
    } finally {
      setStartingBot(false);
    }
  }

  async function toggleFavorite(opponent: Opponent) {
    setBusyOpponentId(opponent.userId);
    try {
      if (opponent.isFavorite) await api.removeFavorite(opponent.userId);
      else await api.addFavorite(opponent.userId);
      setOpponents(
        (prev) =>
          prev?.map((item) =>
            item.userId === opponent.userId ? { ...item, isFavorite: !item.isFavorite } : item,
          ) ?? null,
      );
      setPlayersRefreshKey((key) => key + 1);
    } finally {
      setBusyOpponentId(null);
    }
  }

  function favoriteChangedInPlayers(userId: string, isFavorite: boolean) {
    setOpponents(
      (prev) =>
        prev?.map((item) => (item.userId === userId ? { ...item, isFavorite } : item)) ?? null,
    );
  }

  async function challenge(opponent: Opponent) {
    setBusyOpponentId(opponent.userId);
    try {
      await sendChallenge(opponent);
    } finally {
      setBusyOpponentId(null);
    }
  }

  // Ulubieni na górze, reszta w kolejności z serwera (najświeższa partia pierwsza) —
  // `sort` w silnikach zgodnych z ES2019+ jest stabilny, więc kolejność w obrębie
  // każdej z dwóch grup zostaje nietknięta.
  const sortedOpponents = opponents
    ? [...opponents].sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite))
    : null;

  return (
    <>
      {/* Jedyne dwie drogi do nowej partii (`docs/08-decyzje.md` #25): wyzwanie gracza z listy
          albo bot. Pokój z kodem i kolejka rankingowa zostały zdjęte z interfejsu. */}
      <PlayersCard
        title={`Cześć, ${user?.nickname ?? ''}`}
        actions={
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => botDialogRef.current?.showModal()}
          >
            Zagraj z botem
          </button>
        }
        refreshKey={playersRefreshKey}
        onFavoriteChange={favoriteChangedInPlayers}
      />

      {sortedOpponents && sortedOpponents.length > 0 && (
        <div className="card">
          <h2 className="hand">Przeciwnicy</h2>
          <p className="app__note">
            Ostatnio grani — dodaj do ulubionych albo wyzwij na pojedynek.
          </p>
          <div className="table-scroll">
            <table className="history">
              <thead>
                <tr>
                  <th scope="col" />
                  <th scope="col">Gracz</th>
                  <th scope="col">Bilans</th>
                  <th scope="col">Ostatnio</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {sortedOpponents.map((opponent) => {
                  const busy = busyOpponentId === opponent.userId;
                  // `kind === 'challenge'`: `outgoing` niesie teraz też oferty rewanżu do tego
                  // samego przeciwnika (np. niedawno rozegrana partia) — bez tego filtru wiersz
                  // lobby pokazywałby "Anuluj wyzwanie" dla czegoś, co wyzwaniem nie jest.
                  const pending = outgoing.find(
                    (item) =>
                      item.kind === 'challenge' &&
                      item.to.userId === opponent.userId &&
                      item.status === 'pending',
                  );
                  return (
                    <tr key={opponent.userId}>
                      <td>
                        <button
                          type="button"
                          className="btn btn--ghost btn--small"
                          disabled={busy}
                          aria-label={
                            opponent.isFavorite ? 'Usuń z ulubionych' : 'Dodaj do ulubionych'
                          }
                          onClick={() => void toggleFavorite(opponent)}
                        >
                          {opponent.isFavorite ? '★' : '☆'}
                        </button>
                      </td>
                      <td>{opponent.nickname}</td>
                      <td>
                        {opponent.headToHead.wins}W / {opponent.headToHead.losses}P
                      </td>
                      <td>
                        {opponent.lastPlayedAt
                          ? new Date(opponent.lastPlayedAt).toLocaleDateString('pl-PL')
                          : '—'}
                      </td>
                      <td>
                        {pending ? (
                          <button
                            type="button"
                            className="btn btn--ghost btn--small"
                            onClick={() => void cancelChallenge(pending.id)}
                          >
                            Anuluj wyzwanie
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn--small"
                            disabled={busy}
                            onClick={() => void challenge(opponent)}
                          >
                            Wyzwij
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <dialog ref={botDialogRef} className="bot-dialog">
        <h2 className="hand">Wybierz przeciwnika</h2>
        <div className="bot-dialog__levels">
          {(Object.keys(BOT_LEVEL_LABEL) as BotLevel[]).map((level) => (
            <button
              key={level}
              type="button"
              className="btn"
              onClick={() => void startBotGame(level)}
              disabled={startingBot}
            >
              {BOT_LEVEL_LABEL[level]}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          onClick={() => botDialogRef.current?.close()}
        >
          Anuluj
        </button>
      </dialog>
    </>
  );
}
